/**
 * Type-safe subscription hook (PRD-18 §FR-2.3).
 *
 *   useRealtimeEvent("material.ready", (payload) => {
 *     qc.invalidateQueries({ queryKey: materialKeys.lists() })
 *   })
 *
 * Subscribes on mount, unsubscribes on unmount. The handler ref is
 * kept in a ref so consumers don't have to wrap it in `useCallback`
 * — the subscription survives every render of the parent component
 * without re-binding.
 */

import { useEffect, useRef } from "react"

import { realtimeClient } from "./client"
import type { RealtimeEventPayload, RealtimeEventType } from "./types"

export function useRealtimeEvent<T extends RealtimeEventType>(
  eventType: T,
  handler: (payload: RealtimeEventPayload[T]) => void,
): void {
  const handlerRef = useRef(handler)
  // Keep the ref fresh every render so the subscription always calls
  // the latest closure (captures the latest queryClient, params, etc.)
  // without re-subscribing.
  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  useEffect(() => {
    const unsubscribe = realtimeClient.subscribe(eventType, (payload) => {
      handlerRef.current(payload)
    })
    return unsubscribe
  }, [eventType])
}
